import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../hooks/useAuthContext";
import { msmartAxios } from "../../api/axios";
import { getErrorMessage } from "../../utils/errorMessage";
import { HeaderSkeleton, TabsSkeleton } from "../../components/Skeleton";
import { LeadsBodySkeleton } from "./components/MsmartSkeletons";

function Msmart() {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [err, setErr] = useState("");
  const [position, setPosition] = useState("");
  const [teamId, setTeamId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      if (!user?.token) return;

      try {
        setLoading(true);
        setErr("");

        const res = await msmartAxios.get("/api/msmart/get/user/team", {
          headers: { accessToken: user.token },
        });

        if (!isMounted) return;

        setPosition(res.data?.pos || "");
        setTeamId(res.data?.teamId || "");
      } catch (e) {
        if (!isMounted) return;

        setErr(getErrorMessage(e, "Unable to load your team access. Please try again."));
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    run();
    return () => {
      isMounted = false;
    };
  }, [user?.token]);

  useEffect(() => {
    if (!position || !teamId || err) return;

    if (position.startsWith("Member")) {
      navigate(`/msmart/db/manage/${teamId}`, { replace: true });
    } else if (position.startsWith("Manager")) {
      navigate(`/msmart/team/summary/${teamId}`, { replace: true });
    } else if (position.startsWith("Owner")) {
      navigate(`/msmart/team/admin/summary/${teamId}`, { replace: true });
    } else {
      setErr("Unknown role detected. Please contact admin.");
    }
  }, [position, teamId, err, navigate]);

  const manualGo = () => {
    if (!teamId || !position) return;

    if (position.startsWith("Member")) {
      navigate(`/msmart/db/manage/${teamId}`);
    } else if (position.startsWith("Manager")) {
      navigate(`/msmart/team/summary/${teamId}`);
    } else if (position.startsWith("Owner")) {
      navigate(`/msmart/team/admin/summary/${teamId}`);
    }
  };

  // While resolving the team (and the instant before redirecting), preview the workspace layout.
  if (!err && (loading || (position && teamId))) {
    return (
      <div className="container py-3" role="status" aria-label="Loading your M-SMART workspace">
        <HeaderSkeleton />
        <div className="card mt-2">
          <div className="card-header"><TabsSkeleton count={5} /></div>
          <div className="card-body"><LeadsBodySkeleton /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-7">
          <div className="msmart-wrap">
            <div className="ds-card p-4 p-md-5">
              {/* Top chips */}
              <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <div className="d-flex flex-wrap gap-2">
                  <span className="ds-chip ds-chip-dark">M-SMART</span>
                  <span className="ds-chip">
                    {loading ? "Checking access" : "Access ready"}
                  </span>
                </div>

                {position ? (
                  <span className="ds-chip">
                    Role: <span className="fw-semibold ms-1">{position}</span>
                  </span>
                ) : null}
              </div>

              <div className="mt-4">
                {err ? (
                  <>
                    <h4 className="mb-2">Unable to redirect</h4>
                    <div className="text-muted mb-3">{err}</div>

                    <div className="d-flex flex-wrap gap-2">
                      <button
                        className="btn btn-dark px-4"
                        onClick={() => window.location.reload()}
                      >
                        Refresh
                      </button>

                      <button
                        className="btn btn-outline-dark px-4"
                        onClick={manualGo}
                        disabled={!teamId || !position}
                      >
                        Try Manual Redirect
                      </button>
                    </div>

                    <div className="mt-3 small text-muted">
                      Tip: If you just logged in, try refreshing once. Sometimes the token sync is slightly delayed.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="spinner-border"
                        role="status"
                        aria-label="Loading"
                      />
                      <div>
                        <div className="h5 mb-1">
                          {loading ? "Loading your team..." : "Redirecting..."}
                        </div>
                        <div className="text-muted">
                          Preparing your M-SMART workspace.
                        </div>
                      </div>
                    </div>

                    {/* detail row */}
                    <div className="mt-4 msmart-meta">
                      <div className="d-flex flex-wrap gap-2">
                        <span className="badge ds-badge">
                          Team ID: {teamId || "—"}
                        </span>
                        <span className="badge ds-badge">
                          Status: {loading ? "Fetching" : "Ready"}
                        </span>
                      </div>

                      <div className="mt-3 d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-outline-dark"
                          onClick={manualGo}
                          disabled={loading || !teamId || !position}
                        >
                          Open Now
                        </button>

                        <button
                          className="btn btn-light"
                          onClick={() => window.location.reload()}
                        >
                          Reload
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="text-center text-muted small mt-3">
              If this page stays here too long, click <span className="fw-semibold">Open Now</span>.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Msmart;
